/**
 * REK-1987 (SSO.7): module-level registry bridging Clerk sign-in TICKET
 * redemption into non-React pages.
 *
 * The Braze deep-link pages (`/auth/one-tap`, `/auth/signup`) redeem their
 * Reklaim token against the edge functions and — under
 * `VITE_AUTH_PROVIDER=clerk` — receive a Clerk sign-in token ("ticket")
 * instead of a Supabase magic-link `token_hash`. Redeeming a ticket needs
 * the live clerk-js instance (`signIn.create({ strategy: 'ticket' })` +
 * `setActive`), which only exists once React has mounted
 * `ClerkAuthProvider`. This registry is the seam, mirroring
 * `src/lib/clerk-token.ts`: the provider registers a redeemer once
 * clerk-js is loaded; the pages await it here.
 *
 * Deliberately framework-free (no React imports) and inert under
 * VITE_AUTH_PROVIDER=supabase (nothing ever registers).
 */

/**
 * Flat result shape (not a discriminated union — the SPA tsconfig runs
 * `strict: false`, under which boolean-discriminant narrowing doesn't
 * apply). `code`/`lockedMessage`/`message` are only set when `ok` is false.
 */
export interface ClerkTicketRedemption {
  ok: boolean;
  /**
   * - `not_ready`: clerk-js never became available (provider not
   *   mounted / load failure). The ticket was NOT consumed.
   * - `ticket_failed`: Clerk rejected the redemption (expired/revoked
   *   ticket, banned user, incomplete sign-in status).
   */
  code?: "not_ready" | "ticket_failed";
  /**
   * Set when Clerk rejected the sign-in with `user_banned` — the
   * page surfaces the existing account-locked copy instead of a
   * generic failure (parity with `profiles.is_locked` enforcement).
   */
  lockedMessage?: string | null;
  /** Internal detail for console telemetry — never rendered raw. */
  message?: string;
}

export type ClerkTicketRedeemer = (
  ticket: string,
) => Promise<ClerkTicketRedemption>;

let redeemer: ClerkTicketRedeemer | null = null;
let waiters: Array<(r: ClerkTicketRedeemer) => void> = [];

/**
 * Registered by ClerkAuthProvider once clerk-js is loaded (and cleared on
 * unmount). Registration releases any `redeemClerkTicket` calls that were
 * waiting for the provider to come up.
 */
export function registerClerkTicketRedeemer(
  next: ClerkTicketRedeemer | null,
): void {
  redeemer = next;
  if (next) {
    const pending = waiters;
    waiters = [];
    for (const release of pending) release(next);
  }
}

/**
 * How long `redeemClerkTicket` waits for the provider to register before
 * giving up. clerk-js loads within ~1-2s of app mount; the redeem POST that
 * produces the ticket usually takes longer than that, so in practice the
 * redeemer is already registered. 15s covers slow devices/networks.
 */
const REDEEMER_WAIT_TIMEOUT_MS = 15_000;

function waitForRedeemer(timeoutMs: number): Promise<ClerkTicketRedeemer | null> {
  if (redeemer) return Promise.resolve(redeemer);
  return new Promise((resolve) => {
    const timer = setTimeout(() => {
      waiters = waiters.filter((w) => w !== release);
      resolve(null);
    }, timeoutMs);
    const release = (r: ClerkTicketRedeemer) => {
      clearTimeout(timer);
      resolve(r);
    };
    waiters.push(release);
  });
}

/**
 * Redeem a Clerk sign-in ticket, waiting (bounded) for the provider to
 * register its redeemer first. Never throws — failures come back as a
 * discriminated result so the calling page owns the error UX.
 */
export async function redeemClerkTicket(
  ticket: string,
  options: { timeoutMs?: number } = {},
): Promise<ClerkTicketRedemption> {
  const active = await waitForRedeemer(
    options.timeoutMs ?? REDEEMER_WAIT_TIMEOUT_MS,
  );
  if (!active) {
    return {
      ok: false,
      code: "not_ready",
      lockedMessage: null,
      message: "Clerk ticket redeemer not registered",
    };
  }
  try {
    return await active(ticket);
  } catch (e) {
    return {
      ok: false,
      code: "ticket_failed",
      lockedMessage: null,
      message: e instanceof Error ? e.message : String(e),
    };
  }
}

/** Test-only: reset all registry state between test cases. */
export function __resetClerkTicketRegistryForTests(): void {
  redeemer = null;
  waiters = [];
}
